import hljs from 'highlight.js/lib/core';
import React, { FC, useCallback, useEffect, useRef } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@column/ui';

import { keysToSnakeCase } from '~/util';

export interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

export const EventDetailModal: FC<EventDetailModalProps> = ({ isOpen, onClose, data }) => {
  const codeRef = useRef<HTMLElement | null>(null);

  const highlightCode = useCallback(
    (element: HTMLElement | null) => {
      if (element && isOpen) {
        element.removeAttribute('data-highlighted');
        hljs.highlightElement(element);
      }
    },
    [isOpen]
  );

  // The dialog content mounts after open, so highlight once the code element exists too.
  const codeRefCallback = (element: HTMLElement | null) => {
    codeRef.current = element;
    highlightCode(element);
  };

  useEffect(() => {
    highlightCode(codeRef.current);
  }, [data, highlightCode]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent showCloseButton={false} className="gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="flex-row items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.08]">
          <DialogTitle>Event Details</DialogTitle>
          <DialogDescription className="sr-only">The raw event payload as JSON.</DialogDescription>
          <DialogClose asChild>
            {/* 24px hit area trimmed to the title's 20px line height so the icon centers on the text. */}
            <button
              type="button"
              aria-label="Close"
              className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
            >
              <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </button>
          </DialogClose>
        </DialogHeader>
        <div className="h-[560px] max-h-[min(560px,calc(100dvh-200px))] overflow-y-auto bg-gray-25 px-6 py-5 text-[13px] leading-[1.6] dark:bg-white/[0.02]">
          {/* The highlight.js theme stylesheet is unlayered, so its .hljs background and padding need !important. */}
          <pre className="m-0 p-0">
            <code
              ref={codeRefCallback}
              className="language-json font-[Roboto_Mono,monospace] break-words whitespace-pre-wrap [&.hljs]:bg-transparent! [&.hljs]:p-0!"
            >
              {JSON.stringify(keysToSnakeCase(data), null, 2)}
            </code>
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
};
